<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { MoreVertical, RefreshCw, Share2, ExternalLink, Download } from 'lucide-vue-next'
import ShareOptionsModal from './ShareOptionsModal.vue'

const { t: $t } = useI18n()

const props = defineProps({
  walletAddress: {
    type: String,
    default: null
  },
  claimUrl: {
    type: String,
    default: ''
  },
  emojiPin: {
    type: String,
    default: ''
  },
  explorerUrl: {
    type: String,
    default: null
  },
  hasMnemonic: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['refresh', 'view-explorer', 'show-mnemonic'])

const isOpen = ref(false)
const shareModalOpen = ref(false)

const toggleMenu = () => {
  isOpen.value = !isOpen.value
}

const closeMenu = () => {
  isOpen.value = false
}

const handleRefresh = () => {
  emit('refresh')
  closeMenu()
}

const handleShareInstructions = () => {
  shareModalOpen.value = true
  closeMenu()
}

const closeShareModal = () => {
  shareModalOpen.value = false
}

const handleViewExplorer = () => {
  if (props.walletAddress && props.explorerUrl) {
    const url = `${props.explorerUrl}/address/${props.walletAddress}`
    window.open(url, '_blank', 'noopener,noreferrer')
  }
  closeMenu()
}

const handleShowMnemonic = () => {
  emit('show-mnemonic')
  closeMenu()
}

// Click outside to close
const menuRef = ref(null)

const handleClickOutside = (event) => {
  if (menuRef.value && !menuRef.value.contains(event.target)) {
    closeMenu()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<template>
  <div ref="menuRef" class="card-menu">
    <!-- Burger button -->
    <button
      type="button"
      class="menu-button"
      :class="{ 'is-open': isOpen }"
      @click.stop="toggleMenu"
      :title="$t('cardMenu.options', 'Options')"
    >
      <MoreVertical :size="20" />
    </button>

    <!-- Dropdown menu -->
    <Transition name="menu-fade">
      <div v-if="isOpen" class="menu-dropdown" @click.stop>
        <button
          type="button"
          class="menu-item"
          @click="handleRefresh"
        >
          <RefreshCw :size="16" class="menu-icon" />
          <span>{{ $t('cardMenu.refreshBalance', 'Refresh Balance') }}</span>
        </button>

        <button
          type="button"
          class="menu-item"
          @click="handleShareInstructions"
        >
          <Share2 :size="16" class="menu-icon" />
          <span>{{ $t('cardMenu.shareInstructions', 'Share Instructions') }}</span>
        </button>

        <button
          v-if="walletAddress && explorerUrl"
          type="button"
          class="menu-item"
          @click="handleViewExplorer"
        >
          <ExternalLink :size="16" class="menu-icon" />
          <span>{{ $t('cardMenu.viewExplorer', 'View on Explorer') }}</span>
        </button>

        <button
          v-if="hasMnemonic"
          type="button"
          class="menu-item menu-item-danger"
          @click="handleShowMnemonic"
        >
          <Download :size="16" class="menu-icon" />
          <span>{{ $t('cardMenu.backupWallet', 'Backup Wallet') }}</span>
        </button>
      </div>
    </Transition>

    <!-- Share Options Modal -->
    <ShareOptionsModal
      :open="shareModalOpen"
      :claim-url="claimUrl"
      :emoji-pin="emojiPin"
      :wallet-address="walletAddress"
      @close="closeShareModal"
    />
  </div>
</template>

<style scoped>
.card-menu {
  @apply absolute top-4 left-4 z-10;
}

.menu-button {
  @apply w-9 h-9 rounded-full flex items-center justify-center;
  @apply bg-white/45 backdrop-blur-sm cursor-pointer;
  @apply text-gray-700 transition-all duration-200;
  @apply shadow-lg;
}

.menu-button:hover {
  @apply bg-white border-white;
  @apply shadow-xl;
  transform: scale(1.1);
}

.menu-button.is-open {
  @apply bg-white border-white;
  @apply text-gray-900;
}

.menu-dropdown {
  @apply absolute top-full left-0 mt-2;
  @apply bg-white rounded-lg shadow-xl border border-gray-200;
  @apply min-w-[200px] py-1 overflow-hidden;
}

.menu-item {
  @apply w-full px-4 py-2.5 flex items-center gap-3;
  @apply text-left text-sm font-medium text-gray-700;
  @apply bg-transparent border-none cursor-pointer;
  @apply transition-colors duration-150;
}

.menu-item:hover {
  @apply bg-gray-100;
}

.menu-item-danger {
  @apply text-orange-600;
}

.menu-item-danger:hover {
  @apply bg-orange-50;
}

.menu-icon {
  @apply flex-shrink-0;
}

/* Transition animations */
.menu-fade-enter-active,
.menu-fade-leave-active {
  transition: all 0.2s ease;
}

.menu-fade-enter-from {
  opacity: 0;
  transform: translateY(-8px) scale(0.95);
}

.menu-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.95);
}

.menu-fade-enter-to,
.menu-fade-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
}
</style>
